import React, { useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  ButtonPrimary,
  ButtonSecondary,
  CancelButtonWrapper,
  LinkPrimary,
  NegotiationActions,
  NegotiationActionsRightWrapper,
  OfferStatusWrapper
} from './Styles';
import UserService from '../../../services/UserService';
import MixpanelConfig from '../../../_shared/constants/mixpanelConfig';
import { BidSource } from '../../../_shared/types/auction';
import ROUTES from '../../Routes/constants';
import ReducerConstants from '../../../_shared/constants/reducers';
import convertToCurrency from '../../../_shared/utils/convertToCurrency';
import AddProposalForm from './AddProposalForm';
import ConfirmModal from '../../../_shared/components/ConfirmModal';
import NegotiationService from '../../../services/NegotiationService';
import { acceptOffer, declineOffer } from './NegotiationModalEvents';
import SellerDeclineModal from '../SellerDeclineModal';
import { NegotiationProposalUserType } from '../../../_shared/models/NegotiationProposal';
import { TradeContext } from '../../../_shared/types/trade';
import useNegotiationOfferLabels from '../../../_shared/hooks/trade/useNegotiationOfferLabels';
import useAnalytics, {
  getSellerProperties,
  getVinBasicProperties
} from '../../../_shared/hooks/useAnalytics';
import SegmentConfig from '../../../_shared/constants/segmentConfig';

const ReplyNegotiationForm = ({
  latestOffer,
  myLatestOffer,
  tradeDetails,
  negotiations,
  onAddCounterOffer,
  close,
  tradeContext,
  highBidThreshold,
  setOnAccept,
  onNegotiationEnded,
  isOmp
}) => {
  const {
    topBid,
    sellerNegotiated,
    isGreyOut,
    negotiationState,
    id: tradeId,
    auctionId,
    isInternalTrade
  } = tradeDetails;

  const track = useAnalytics();
  const intl = useIntl();
  const [isCountering, setIsCountering] = useState(false);
  const [isStayingFirm, setIsStayingFirm] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmModalLogic, setConfirmModalLogic] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSellerDeclineModalOpen, setIsSellerDeclineModalOpen] = useState(
    false
  );

  const [offerTitle] = useNegotiationOfferLabels(
    negotiationState,
    tradeContext,
    isGreyOut
  );

  const negotiationService = NegotiationService.get();
  const userService = UserService.get();
  const isSellContext = tradeContext === TradeContext.Sell;

  const user = useSelector(
    state => state[ReducerConstants.AUTH]?.user,
    shallowEqual
  );
  const eventSource = window.location.href.includes(ROUTES.TRADE_URI)
    ? BidSource.VDP_OVERLAY
    : BidSource.LIST_VIEW;

  const getDeclineDescriptionMessageId = () => {
    if (isInternalTrade) {
      return 'modal.negotiation.decline.dgn.description';
    }
    if (isSellContext) {
      return `modal.negotiation.seller.decline.description`;
    }
    return 'modal.negotiation.buyer.decline.description';
  };

  const negotiationModalEventVariables = {
    setConfirmModalLogic,
    negotiationService,
    close,
    sellerLatestOffer: myLatestOffer,
    latestOffer,
    tradeDetails,
    setIsConfirmModalOpen,
    msgId: getDeclineDescriptionMessageId(),
    intl,
    setOnAccept,
    setIsLoading,
    isSellContext,
    user,
    isSecondBidder: false,
    onNegotiationEnded,
    track
  };

  const acceptOfferLocal = () => acceptOffer(negotiationModalEventVariables);

  const declineOfferLocal = () => declineOffer(negotiationModalEventVariables);

  const onCounterButtonClick = () => {
    setIsCountering(true);
    track(SegmentConfig.names.pendingCounterOfferClicked, {
      ...getVinBasicProperties(tradeDetails),
      ...getSellerProperties(tradeDetails),
      event_state_type: userService.isSeller(user, tradeDetails?.dealer?.id)
        ? SegmentConfig.eventStateType.sellPending
        : SegmentConfig.eventStateType.buyPending
    });
    track(MixpanelConfig.names.counterOffer, {
      user_type: userService.isSeller(user, tradeDetails?.dealer?.id)
        ? MixpanelConfig.userType.seller
        : MixpanelConfig.userType.buyer,
      source: eventSource,
      trade_id: tradeId,
      auction_id: auctionId
    });
  };

  const sellerDeclineModal = () => {
    setIsSellerDeclineModalOpen(true);
  };

  const sellerNotNegotiated = () => {
    if (!isSellContext) {
      return false;
    }

    // In case sellerNegotiated is not present (VDP), we'd need to check current proposals.
    const sellerHasNegotiated =
      sellerNegotiated ||
      negotiations?.proposals.some(
        p => p.initiator === NegotiationProposalUserType.SELLER
      );

    return !sellerHasNegotiated;
  };

  const handleStayFirm = () => {
    setIsStayingFirm(true);

    track(SegmentConfig.names.pendingStayFirmClicked, {
      ...getVinBasicProperties(tradeDetails),
      ...getSellerProperties(tradeDetails),
      event_state_type: userService.isSeller(user, tradeDetails?.dealer?.id)
        ? SegmentConfig.eventStateType.sellPending
        : SegmentConfig.eventStateType.buyPending
    });
  };

  const handleDeclineConfirm = () => {
    if (sellerNotNegotiated()) {
      sellerDeclineModal();
    } else {
      declineOfferLocal();
    }
  };

  return (
    <>
      <OfferStatusWrapper>
        <div id="offer" data-testid="reply-negotiation-form-offer">
          <p>{offerTitle}</p>
          <span>{convertToCurrency(latestOffer?.price)}</span>
        </div>
        {!(isCountering || isStayingFirm) && (
          <ButtonPrimary
            onClick={acceptOfferLocal}
            data-testid="reply-negotiation-form-accept-button"
          >
            <FormattedMessage id="modal.negotiation.accept" />
          </ButtonPrimary>
        )}
      </OfferStatusWrapper>
      {isCountering || isStayingFirm ? (
        <>
          <CancelButtonWrapper>
            <LinkPrimary
              data-testid="reply-negotiation-form-cancel-button"
              onClick={() =>
                isCountering ? setIsCountering(false) : setIsStayingFirm(false)
              }
            >
              <FormattedMessage id="modal.cancel" />
            </LinkPrimary>
          </CancelButtonWrapper>
          <AddProposalForm
            tradeContext={tradeContext}
            isCountering={isCountering}
            isStayingFirm={isStayingFirm}
            myLatestOffer={myLatestOffer}
            tradeDetails={tradeDetails}
            negotiations={negotiations}
            onAddProposal={onAddCounterOffer}
            highBidThreshold={highBidThreshold}
            close={close}
          />
        </>
      ) : (
        <NegotiationActions>
          <ButtonSecondary
            onClick={handleDeclineConfirm}
            data-testid="reply-negotiation-form-decline-button"
          >
            <FormattedMessage id="modal.negotiation.decline" />
          </ButtonSecondary>
          <NegotiationActionsRightWrapper>
            {!isSellContext && (
              <ButtonSecondary
                onClick={handleStayFirm}
                data-testid="reply-negotiation-form-stayFirm-button"
              >
                <FormattedMessage id="modal.negotiation.stayFirm" />
              </ButtonSecondary>
            )}
            <ButtonSecondary
              onClick={onCounterButtonClick}
              data-testid="reply-negotiation-form-counter-button"
            >
              <FormattedMessage id="modal.negotiation.counter" />
            </ButtonSecondary>
          </NegotiationActionsRightWrapper>
        </NegotiationActions>
      )}
      <ConfirmModal
        modalLogic={confirmModalLogic}
        isOpen={isConfirmModalOpen}
        isLoading={isLoading}
        loadingMsg={<FormattedMessage id="modal.confirmation.loading" />}
      />

      <SellerDeclineModal
        trade={tradeDetails}
        topBid={topBid}
        isOpen={isSellerDeclineModalOpen}
        onCancel={setIsSellerDeclineModalOpen}
        isOmp={isOmp}
      />
    </>
  );
};

export default ReplyNegotiationForm;
